import { useState, useEffect, useCallback } from 'react';
import { Type, Upload, Image, Palette, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { getSettings, updateSettings } from '@/api/settings';

export default function SettingsPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    banner: '',
    primaryColor: '#0ea5e9',
    welcomeMessage: '',
  });

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getSettings();
      setFormData({
        name: result.name || '',
        logo: result.logo || '',
        banner: result.banner || '',
        primaryColor: result.primaryColor || '#0ea5e9',
        welcomeMessage: result.welcomeMessage || '',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t.common.error]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSettings(formData);
      toast({
        title: t.common.success,
        description: t.admin.settingsSaved,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (color: string) => {
    setFormData({ ...formData, primaryColor: color });
  };

  const presetColors = [
    '#0ea5e9', // Sky blue
    '#8b5cf6', // Purple
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#ec4899', // Pink
    '#6366f1', // Indigo
    '#14b8a6', // Teal
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.admin.settings}</h1>
          <p className="text-gray-500 mt-1">{t.admin.settingsDesc}</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? t.common.loading : t.admin.saveChanges}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                {t.admin.basicInfo}
              </CardTitle>
              <CardDescription>{t.admin.basicInfoDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label={t.admin.orgName}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.admin.orgNamePlaceholder}
              />
              <Textarea
                label={t.admin.welcomeMessage}
                value={formData.welcomeMessage}
                onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                placeholder={t.admin.welcomeMessagePlaceholder}
                rows={3}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                {t.admin.branding}
              </CardTitle>
              <CardDescription>{t.admin.brandingDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.admin.logo}</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                    {formData.logo ? (
                      <img
                        src={formData.logo}
                        alt="Logo"
                        className="w-full h-full object-contain rounded-xl"
                      />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder={t.admin.logoUrl}
                      value={formData.logo}
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t.admin.logoSize}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.admin.banner}</label>
                <div className="space-y-2">
                  <div className="w-full h-32 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                    {formData.banner ? (
                      <img
                        src={formData.banner}
                        alt="Banner"
                        className="w-full h-full object-cover rounded-xl"
                      />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                        <p className="text-sm text-gray-500 mt-2">{t.admin.addBanner}</p>
                      </div>
                    )}
                  </div>
                  <Input
                    placeholder={t.admin.logoUrl}
                    value={formData.banner}
                    onChange={(e) => setFormData({ ...formData, banner: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">{t.admin.bannerSize}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                {t.admin.themeColor}
              </CardTitle>
              <CardDescription>{t.admin.themeColorDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-4">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorChange(color)}
                    className={`w-10 h-10 rounded-lg transition-transform hover:scale-110 ${
                      formData.primaryColor === color
                        ? 'ring-2 ring-offset-2 ring-gray-400'
                        : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg border border-gray-200"
                  style={{ backgroundColor: formData.primaryColor }}
                />
                <Input
                  value={formData.primaryColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  placeholder="#0ea5e9"
                  className="flex-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div>
          <Card className="sticky top-8">
            <CardHeader>
              <CardTitle>{t.admin.preview}</CardTitle>
              <CardDescription>{t.admin.previewDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-xl overflow-hidden bg-gray-50">
                {/* Mock header */}
                <div
                  className="h-24 flex items-end px-4 pb-3"
                  style={{
                    background: formData.banner
                      ? `url(${formData.banner}) center/cover`
                      : `linear-gradient(135deg, ${formData.primaryColor}, ${formData.primaryColor}dd)`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center">
                      {formData.logo ? (
                        <img
                          src={formData.logo}
                          alt="Logo"
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: formData.primaryColor }}
                        />
                      )}
                    </div>
                    <div className="text-white">
                      <h3 className="font-bold text-lg drop-shadow">{formData.name || t.admin.orgName}</h3>
                    </div>
                  </div>
                </div>

                {/* Welcome message */}
                <div className="p-4">
                  <p className="text-gray-600 text-sm mb-4">
                    {formData.welcomeMessage || t.admin.welcomeMessagePlaceholder}
                  </p>

                  {/* Mock app cards */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{ backgroundColor: `${formData.primaryColor}20` }}
                      />
                      <div className="flex-1">
                        <div className="h-3 w-24 bg-gray-200 rounded mb-1.5" />
                        <div className="h-2 w-32 bg-gray-100 rounded" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{ backgroundColor: `${formData.primaryColor}20` }}
                      />
                      <div className="flex-1">
                        <div className="h-3 w-20 bg-gray-200 rounded mb-1.5" />
                        <div className="h-2 w-28 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
